import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import {
  useDealerLookup,
  useUserAccessCountries,
  useUserAccessDealers,
  type UserAccessScope,
} from '@/pages/stla/useUserAccess';
import { useUserDetails, type AccessScopeAssignment, type UserListItem } from './useUsers';

/**
 * Create / edit form for a user account.
 *
 * One component for both, as the Razor page's two modals were the same fields
 * twice — the only differences are the title, whether the account can be
 * deactivated, and that editing does not re-send the invitation.
 *
 * There is no password field in either mode, and that is not an omission: an
 * administrator creates the account without one and the server mails a
 * set-password link (`UserManagementService.CreateUserAsync`). It is the same
 * reason there is no self-registration screen.
 *
 * Both modes carry the same two panes — the account, then the data it may see —
 * but they move between them differently. Creating is a wizard, because the
 * scopes a role may be given are not known until the roles are chosen. Editing
 * shows them as tabs with a single Save, so changing a surname does not mean
 * clicking through a step about dealer visibility.
 *
 * The standalone User Access screen is unchanged and still manages the same
 * rules; nothing here replaces it.
 */

/** Both scope types, in the order the pickers offer them. */
const ALL_SCOPES: readonly UserAccessScope[] = ['Country', 'Dealer'];

export interface UserFormValues {
  userName: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  isActive: boolean;
  /**
   * Round-tripped, not defaulted. `UserManagementService.UpdateUserAsync` assigns
   * `user.EmailConfirmed = request.EmailConfirmed` unconditionally, so omitting
   * it from the payload would silently un-confirm the address on every edit.
   */
  emailConfirmed: boolean;
  /**
   * The rules the account should end up with.
   *
   * Undefined means "not known yet" — an edit whose existing rules are still
   * being fetched — and must be sent as an omitted field rather than an empty
   * array, which the server reads as "remove them all". Creating starts at `[]`,
   * because a new account provably has none.
   */
  accessScopes?: AccessScopeAssignment[];
}

export interface UserFormModalProps {
  /** The user being edited, or null to create one. */
  user: UserListItem | null;
  availableRoles: string[];
  /** Per role, the scope types it may be given; from `GET /api/users/bootstrap`. */
  roleScopes: Record<string, UserAccessScope[]>;
  /** Roles that see everything and need no rule — `Permissions.DataScope.Full`. */
  fullAccessRoles: string[];
  pending: boolean;
  error: unknown;
  onCancel: () => void;
  onSubmit: (values: UserFormValues) => void;
}

const EMPTY: UserFormValues = {
  userName: '',
  email: '',
  firstName: '',
  lastName: '',
  roles: [],
  isActive: true,
  emailConfirmed: false,
  accessScopes: [],
};

/** The only fields that render a message of their own, under their input. */
const ANCHORED_FIELDS = ['userName', 'email'] as const;

/**
 * A failed submission split into what goes under an input and what goes above
 * the form.
 *
 * Create and update do not fail the same way. Update returns ProblemDetails, so
 * the body carries `errors` keyed by field and, for the rest, a `detail` string.
 * Create answers a 400 with a `CreateUserResponse` instead, whose `errors` is a
 * flat `string[]` — "Username already exists", a rejected access scope — and
 * reading only `ApiError.message` there would show "Request failed with status
 * 400" and throw the reason away.
 *
 * Anything keyed to a field this form does not render — a role, a scope, a
 * nested model-state key — is reported above the form rather than dropped: an
 * error the server bothered to send must not vanish because no input claimed it.
 */
function splitErrors(error: unknown): {
  fields: Record<string, string>;
  general: string[];
} {
  if (!(error instanceof ApiError)) return { fields: {}, general: [] };

  const raw = error.problem?.errors;

  if (Array.isArray(raw)) {
    const messages = raw.filter((message): message is string => typeof message === 'string');
    return {
      fields: {},
      general: messages.length > 0 ? messages : [error.message],
    };
  }

  const fields: Record<string, string> = {};
  const general: string[] = [];

  for (const [key, messages] of Object.entries(raw ?? {})) {
    // ASP.NET reports model-state keys capitalised; the inputs are camelCase.
    const field = key.charAt(0).toLowerCase() + key.slice(1);
    const text = messages.join(' ');

    if ((ANCHORED_FIELDS as readonly string[]).includes(field)) {
      fields[field] = text;
    } else {
      general.push(text);
    }
  }

  if (Object.keys(fields).length === 0 && general.length === 0) general.push(error.message);

  return { fields, general };
}

export function UserFormModal({
  user,
  availableRoles,
  roleScopes,
  fullAccessRoles,
  pending,
  error,
  onCancel,
  onSubmit,
}: UserFormModalProps) {
  const { t } = useTranslation();
  const isEdit = user != null;

  const [values, setValues] = useState<UserFormValues>(EMPTY);
  /** Which pane is on screen. */
  const [step, setStep] = useState<'account' | 'access'>('account');

  useEffect(() => {
    setValues(
      user
        ? {
            userName: user.userName ?? '',
            email: user.email ?? '',
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            roles: user.roles,
            isActive: user.isActive,
            emailConfirmed: user.emailConfirmed,
            // Not [] — that would tell the server to delete every rule the
            // account has if Save were pressed before the fetch below returns.
            accessScopes: undefined,
          }
        : EMPTY,
    );
    setStep('account');
  }, [user]);

  // The grid row has no rules on it — only `GET /api/users/{id}` carries them —
  // so editing loads them separately. The same query the details modal uses, so
  // opening one after the other costs a single request.
  const details = useUserDetails(user?.id ?? null);
  const scopesLoadedFor = useRef<string | null>(null);

  useEffect(() => {
    const loaded = details.data;
    if (!loaded || scopesLoadedFor.current === loaded.id) return;

    // Guarded by the id rather than run once: it must not overwrite scopes the
    // administrator has already edited while the request was in flight, and it
    // must still refill when the modal is reused for a different account.
    scopesLoadedFor.current = loaded.id;
    setValues((current) => ({
      ...current,
      accessScopes: loaded.accessScopes ?? [],
    }));
  }, [details.data]);

  const { fields: errors, general } = splitErrors(error);

  /**
   * One of the roles sees everything, so there is no perimeter to ask about.
   *
   * Checked before the scope types, and it wins over them: an account that
   * already sees all data cannot be narrowed by adding a rule, so a picker here
   * would misrepresent what the rule does.
   */
  const hasFullAccess = useMemo(
    () => values.roles.some((role) => fullAccessRoles.includes(role)),
    [values.roles, fullAccessRoles],
  );

  /**
   * The scope types the chosen roles allow: the union of the `DataScope`
   * permissions their roles carry, as `roleScopes` reports them.
   *
   * Deliberately the same arithmetic the server does, including the unforgiving
   * parts — a role the map does not mention contributes nothing, and no roles at
   * all means nothing is assignable. Being more generous here would only offer
   * choices `POST /api/users/create` then refuses.
   */
  const allowedScopes = useMemo<UserAccessScope[]>(() => {
    const allowed = new Set<UserAccessScope>();
    for (const role of values.roles) {
      for (const scope of roleScopes[role] ?? []) allowed.add(scope);
    }
    return ALL_SCOPES.filter((scope) => allowed.has(scope));
  }, [values.roles, roleScopes]);

  /**
   * Listed rules the current roles do not justify.
   *
   * Reached two ways: changing the roles after adding a rule, and editing an
   * account whose rules predate the permission — the standalone User Access
   * screen grants them without consulting any of this. They are neither hidden
   * nor quietly dropped, because dropping them would delete real access on the
   * next Save without anyone seeing it. They block Save until removed instead.
   *
   * None of that applies under full access: a rule on an account that already sees
   * everything is redundant, not wrong, and promoting someone to Admin should not
   * demand they first clear the rules their old role was given.
   */
  const conflictingScopes = useMemo(
    () =>
      hasFullAccess
        ? []
        : (values.accessScopes ?? []).filter((scope) => !allowedScopes.includes(scope.scopeType)),
    [hasFullAccess, values.accessScopes, allowedScopes],
  );

  const set = <K extends keyof UserFormValues>(key: K, value: UserFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  const toggleRole = (role: string) =>
    setValues((current) => ({
      ...current,
      roles: current.roles.includes(role)
        ? current.roles.filter((r) => r !== role)
        : [...current.roles, role],
    }));

  const title = isEdit ? t('Users.Edit', 'Edit user') : t('Users.Create', 'Create user');

  return (
    // Wide in both modes: the data-access pane lays a picker, a value and a
    // button out in one row, and it is the same pane whether creating or editing.
    <Modal show onHide={onCancel} centered size="lg" backdrop={pending ? 'static' : true}>
      <Modal.Header closeButton={!pending}>
        <Modal.Title>
          {title}
          {!isEdit && (
            <span className="text-muted fs-6 ms-2">
              {step === 'account'
                ? t('Users.Step1', 'Step 1 of 2 — Account')
                : t('Users.Step2', 'Step 2 of 2 — Data access')}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          // While creating, the submit button on the first pane reads "Next":
          // submitting is what runs the browser's own validation of the required
          // fields, so advancing through it keeps `required` meaningful.
          if (!isEdit && step === 'account') {
            setStep('access');
            return;
          }
          onSubmit(values);
        }}
      >
        <Modal.Body>
          {/* Tabs only when editing. While creating, the scopes on offer depend on
              roles that have not been chosen yet, so the panes are ordered. */}
          {isEdit && (
            <ul className="nav nav-tabs mb-3">
              {(['account', 'access'] as const).map((pane) => (
                <li className="nav-item" key={pane}>
                  <button
                    type="button"
                    className={`nav-link${step === pane ? ' active' : ''}`}
                    onClick={() => setStep(pane)}
                  >
                    {pane === 'account'
                      ? t('Users.Tab.Account', 'Account')
                      : t('Users.Tab.Access', 'Data access')}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {general.map((message) => (
            <div className="alert alert-danger" role="alert" key={message}>
              {message}
            </div>
          ))}

          {/* On both panes: the cause is a role on one and the rule on the other. */}
          {conflictingScopes.length > 0 && (
            <div className="alert alert-warning" role="alert">
              {t(
                'Users.Access.Conflict',
                'The data access below is not allowed by the selected roles: {{rules}}. Remove it, or give a role the matching data-scope permission.',
                {
                  rules: conflictingScopes
                    .map((scope) => `${scope.scopeType} ${scope.scopeValue}`)
                    .join(', '),
                },
              )}
            </div>
          )}

          {step === 'account' ? (
            <AccountStep
              values={values}
              errors={errors}
              isEdit={isEdit}
              availableRoles={availableRoles}
              onChange={set}
              onToggleRole={toggleRole}
            />
          ) : (
            <AccessStep
              scopes={values.accessScopes ?? []}
              allowedScopes={allowedScopes}
              noRoles={values.roles.length === 0}
              fullAccess={hasFullAccess}
              loading={values.accessScopes === undefined}
              loadFailed={details.isError}
              onChange={(scopes) => set('accessScopes', scopes)}
            />
          )}
        </Modal.Body>

        <Modal.Footer>
          {!isEdit && step === 'access' ? (
            <button
              type="button"
              className="btn btn-secondary me-auto"
              onClick={() => setStep('account')}
              disabled={pending}
            >
              {t('Back', 'Back')}
            </button>
          ) : null}

          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={pending}>
            {t('Cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            // Never blocks "Next": on create the offending rule lives on the pane
            // that button leads to, so blocking it would strand the form.
            disabled={pending || ((isEdit || step === 'access') && conflictingScopes.length > 0)}
            title={
              conflictingScopes.length > 0
                ? t(
                    'Users.Access.ConflictHint',
                    'Remove the rules the selected roles do not allow before saving.',
                  )
                : undefined
            }
          >
            {pending && <span className="spinner-border spinner-border-sm me-2" role="status" />}
            {isEdit
              ? t('Save', 'Save')
              : step === 'account'
                ? t('Next', 'Next')
                : t('Create', 'Create')}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

/** Step one: who the account is and what it may do. The original form, unchanged. */
function AccountStep({
  values,
  errors,
  isEdit,
  availableRoles,
  onChange,
  onToggleRole,
}: {
  values: UserFormValues;
  errors: Record<string, string>;
  isEdit: boolean;
  availableRoles: string[];
  onChange: <K extends keyof UserFormValues>(key: K, value: UserFormValues[K]) => void;
  onToggleRole: (role: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <div className="mb-3">
        <label className="form-label" htmlFor="user-username">
          {t('Users.UserName', 'User name')}
        </label>
        <input
          id="user-username"
          className={`form-control${errors.userName ? ' is-invalid' : ''}`}
          value={values.userName}
          onChange={(event) => onChange('userName', event.target.value)}
          required
          autoFocus
        />
        {errors.userName && <div className="invalid-feedback">{errors.userName}</div>}
      </div>

      <div className="mb-3">
        <label className="form-label" htmlFor="user-email">
          {t('Users.Email', 'Email')}
        </label>
        <input
          id="user-email"
          type="email"
          className={`form-control${errors.email ? ' is-invalid' : ''}`}
          value={values.email}
          onChange={(event) => onChange('email', event.target.value)}
          required
        />
        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
        {!isEdit && (
          <div className="form-text">
            {t(
              'Users.CreateEmailHint',
              'The account is created without a password; a link to set one is emailed to this address.',
            )}
          </div>
        )}
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label" htmlFor="user-firstname">
            {t('Users.FirstName', 'First name')}
          </label>
          <input
            id="user-firstname"
            className="form-control"
            value={values.firstName}
            onChange={(event) => onChange('firstName', event.target.value)}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label" htmlFor="user-lastname">
            {t('Users.LastName', 'Last name')}
          </label>
          <input
            id="user-lastname"
            className="form-control"
            value={values.lastName}
            onChange={(event) => onChange('lastName', event.target.value)}
          />
        </div>
      </div>

      <fieldset className="mb-3">
        <legend className="form-label fs-6">{t('Users.Roles', 'Roles')}</legend>
        {availableRoles.map((role) => (
          <div className="form-check" key={role}>
            <input
              className="form-check-input"
              type="checkbox"
              id={`user-role-${role}`}
              checked={values.roles.includes(role)}
              onChange={() => onToggleRole(role)}
            />
            <label className="form-check-label" htmlFor={`user-role-${role}`}>
              {role}
            </label>
          </div>
        ))}
      </fieldset>

      {/* Only on edit: a new account is always created active, and its
          address is confirmed by the user following the set-password link. */}
      {isEdit && (
        <>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="user-isactive"
              checked={values.isActive}
              onChange={(event) => onChange('isActive', event.target.checked)}
            />
            <label className="form-check-label" htmlFor="user-isactive">
              {t('Users.IsActive', 'Account is active')}
            </label>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="user-emailconfirmed"
              checked={values.emailConfirmed}
              onChange={(event) => onChange('emailConfirmed', event.target.checked)}
            />
            <label className="form-check-label" htmlFor="user-emailconfirmed">
              {t('Users.EmailConfirmed', 'Email address confirmed')}
            </label>
          </div>
        </>
      )}
    </>
  );
}

/**
 * Step two: the data the account may see.
 *
 * The same rules the User Access screen creates one at a time, collected here so
 * a new account arrives with its perimeter already set instead of seeing nothing
 * until someone remembers the other screen. Which scope types are on offer comes
 * from the roles picked in step one — a Dealer User can only be scoped to
 * dealers, and an Admin, holding `DataScope.Full`, is asked for nothing.
 */
function AccessStep({
  scopes,
  allowedScopes,
  noRoles,
  fullAccess,
  loading,
  loadFailed,
  onChange,
}: {
  scopes: AccessScopeAssignment[];
  allowedScopes: UserAccessScope[];
  /** No role is chosen yet, which is why nothing is on offer. */
  noRoles: boolean;
  /** A role sees all data, so no perimeter applies. */
  fullAccess: boolean;
  /** The account's existing rules are not known yet. */
  loading: boolean;
  /** Fetching them failed, so they cannot be edited safely. */
  loadFailed: boolean;
  onChange: (scopes: AccessScopeAssignment[]) => void;
}) {
  const { t } = useTranslation();
  const listId = useId();

  const [draftType, setDraftType] = useState<UserAccessScope>(allowedScopes[0] ?? 'Country');
  const [draftValue, setDraftValue] = useState('');

  // The roles can rule out the type being drafted; fall back to one still on offer.
  useEffect(() => {
    if (allowedScopes.length > 0 && !allowedScopes.includes(draftType)) {
      setDraftType(allowedScopes[0]);
      setDraftValue('');
    }
  }, [allowedScopes, draftType]);

  const countries = useUserAccessCountries(allowedScopes.includes('Country'));
  // Thousands of rows: only worth fetching once a dealer is actually being picked.
  const dealers = useUserAccessDealers(draftType === 'Dealer');

  const duplicate = scopes.some(
    (scope) =>
      scope.scopeType === draftType &&
      scope.scopeValue.toUpperCase() === draftValue.trim().toUpperCase(),
  );

  // Checked against the campaign tables, not against the suggestion list: that list
  // is capped, so a real dealer past the cap would be refused by a local check.
  const lookup = useDealerLookup();
  const [addError, setAddError] = useState<string | null>(null);

  const canAdd = draftValue.trim().length > 0 && !duplicate && !lookup.isPending;

  const add = async () => {
    if (!canAdd) return;
    const value = draftValue.trim();
    setAddError(null);

    if (draftType !== 'Dealer') {
      onChange([...scopes, { scopeType: draftType, scopeValue: value }]);
      setDraftValue('');
      return;
    }

    try {
      const found = await lookup.mutateAsync(value);

      if (!found.exists || found.vatCode == null) {
        setAddError(
          t('Users.Access.UnknownDealer', 'No dealer with VAT code “{{code}}” exists.', {
            code: value,
          }),
        );
        return;
      }

      // The stored spelling, not what was typed: the rule is matched to dealers with
      // a case-sensitive comparison, so "it123" against a stored "IT123" would grant
      // access to nothing at all.
      onChange([...scopes, { scopeType: 'Dealer', scopeValue: found.vatCode }]);
      setDraftValue('');
    } catch {
      setAddError(t('Users.Access.LookupFailed', 'The dealer could not be checked. Try again.'));
    }
  };

  const remove = (index: number) => onChange(scopes.filter((_, i) => i !== index));

  // Nothing may be edited until the rules already in place are known: the form
  // submits the whole set, so building one on top of an unknown starting point
  // would delete whatever it failed to see.
  if (loadFailed) {
    return (
      <div className="alert alert-danger mb-0" role="alert">
        {t(
          'Users.Access.LoadFailed',
          'The current data access could not be loaded, so it cannot be edited here. Saving will leave it untouched; use the User Access screen instead.',
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-muted">
        <span className="spinner-border spinner-border-sm me-2" role="status" />
        {t('Loading', 'Loading...')}
      </div>
    );
  }

  // Full access: nothing to ask. Any rules the account still carries are listed
  // anyway, with the point made that they change nothing — they are real rows in
  // the table, and an administrator looking for why a rule "does not work" should
  // find it here rather than wonder where it went.
  if (fullAccess) {
    return (
      <>
        <div className={`alert alert-info${scopes.length > 0 ? '' : ' mb-0'}`} role="status">
          {t(
            'Users.Access.FullAccess',
            'The selected roles see all data, so there is no scope to choose here.',
          )}
        </div>

        {scopes.length > 0 && (
          <>
            <p className="text-muted">
              {t(
                'Users.Access.FullAccessExisting',
                'These rules are kept but have no effect while the account holds a role with full access:',
              )}
            </p>
            <ScopeList scopes={scopes} allowedScopes={allowedScopes} onRemove={remove} />
          </>
        )}
      </>
    );
  }

  const isCountry = draftType === 'Country';
  // The suggestions come from the campaign tables, which can legitimately be
  // empty — a fresh environment, a campaign not yet imported. The field stays a
  // free-text combobox so that never blocks granting access; the datalist only
  // ever offers help.
  const suggestions = isCountry
    ? (countries.data ?? []).map((code) => ({ value: code, label: code }))
    : (dealers.data ?? []).map((dealer) => ({
        value: dealer.vatCode,
        label: `${dealer.companyName} (${dealer.countryCode})`,
      }));

  return (
    <>
      {/* The explanation of what a scope does belongs only where one can be
          chosen; above the "nothing to choose" notice it just contradicts it. */}
      {allowedScopes.length > 0 && (
        <p className="text-muted">
          {t(
            'Users.Access.Help',
            'Choose what this user will be able to see. Country grants every dealer of that country; Dealer grants a single dealer. Several rules add up.',
          )}
        </p>
      )}

      {/* No add row when nothing may be added, but the rules already there are
          still listed below — they are what the account can see today, and
          hiding them would hide the only control that removes them. */}
      {allowedScopes.length === 0 ? (
        <div className="alert alert-info" role="status">
          {noRoles
            ? t(
                'Users.Access.NoRoles',
                'No role is selected yet. Choose one on the Account tab — which scopes can be granted depends on the data-scope permissions its roles carry.',
              )
            : t(
                'Users.Access.NoPermission',
                'None of the selected roles carries a data-scope permission, so no access can be added here. Grant Permissions.DataScope.Country or Permissions.DataScope.Dealer to a role first.',
              )}
        </div>
      ) : (
        /* Not `align-items-end`: the hint under the value field makes that column
           taller than the others, and aligning on the bottom edge dropped the Add
           button below the input it belongs to. Each column now stacks label over
           control from the top, so the controls line up whether or not the hint is
           there, and Add is joined to the input it acts on. */
        <div className="row g-2 mb-3">
          {/* With one type on offer the select would be a control with no choice in
            it; the badge below still says which type each rule is. */}
          {allowedScopes.length > 1 && (
            <div className="col-sm-4">
              <label className="form-label" htmlFor={`${listId}-type`}>
                {t('Users.Access.ScopeType', 'Scope')}
              </label>
              <select
                id={`${listId}-type`}
                className="form-select"
                value={draftType}
                onChange={(event) => {
                  setDraftType(event.target.value as UserAccessScope);
                  setDraftValue('');
                }}
              >
                {allowedScopes.map((scope) => (
                  <option key={scope} value={scope}>
                    {scope === 'Country'
                      ? t('Users.Access.Country', 'Country')
                      : t('Users.Access.Dealer', 'Dealer')}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={allowedScopes.length > 1 ? 'col-sm-8' : 'col-12'}>
            <label className="form-label" htmlFor={`${listId}-value`}>
              {isCountry
                ? t('Users.Access.CountryCode', 'Country code')
                : t('Users.Access.VatCode', 'Dealer VAT code')}
            </label>
            <div className="input-group">
              <input
                id={`${listId}-value`}
                className="form-control"
                list={`${listId}-options`}
                value={draftValue}
                onChange={(event) => {
                  setDraftValue(event.target.value);
                  // The rejection belongs to the code that was typed, not the field.
                  setAddError(null);
                }}
                // Enter would otherwise submit the whole form, saving the account
                // without the rule being typed at that moment.
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void add();
                  }
                }}
                placeholder={
                  isCountry
                    ? t('Users.Access.CountryPlaceholder', 'ISO code, e.g. IT')
                    : t('Users.Access.VatPlaceholder', 'Search by name or VAT code…')
                }
                autoComplete="off"
              />
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => void add()}
                disabled={!canAdd}
              >
                {lookup.isPending && (
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                )}
                {t('Users.Access.Add', 'Add')}
              </button>
            </div>
            <datalist id={`${listId}-options`}>
              {suggestions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </datalist>
            {addError ? (
              <div className="form-text text-danger">{addError}</div>
            ) : duplicate ? (
              <div className="form-text text-warning">
                {t('Users.Access.Duplicate', 'That rule is already in the list.')}
              </div>
            ) : (
              suggestions.length === 0 && (
                <div className="form-text">
                  {isCountry
                    ? t(
                        'Users.Access.NoCountries',
                        'No country codes available to suggest — type the ISO code.',
                      )
                    : t(
                        'Users.Access.NoDealers',
                        'No dealers available to suggest — type the VAT code.',
                      )}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {scopes.length === 0 ? (
        <div className="alert alert-warning mb-0" role="status">
          {t(
            'Users.Access.Empty',
            'No access yet: the user will not see any data until a rule is added. You can add rules now, or later from the User Access screen.',
          )}
        </div>
      ) : (
        <ScopeList scopes={scopes} allowedScopes={allowedScopes} onRemove={remove} />
      )}
    </>
  );
}

/**
 * The rules currently listed, each with the control that removes it.
 *
 * Shared by the two paths that show rules — the editable step and the full-access
 * note — so a rule looks the same in both and only the surrounding explanation
 * changes.
 */
function ScopeList({
  scopes,
  allowedScopes,
  onRemove,
}: {
  scopes: AccessScopeAssignment[];
  allowedScopes: UserAccessScope[];
  onRemove: (index: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <ul className="list-group">
      {scopes.map((scope, index) => {
        const conflicts = !allowedScopes.includes(scope.scopeType);

        return (
          <li
            className={`list-group-item d-flex align-items-center justify-content-between${
              conflicts ? ' list-group-item-warning' : ''
            }`}
            key={`${scope.scopeType}-${scope.scopeValue}`}
          >
            <span>
              <span
                className={`badge me-2 ${scope.scopeType === 'Country' ? 'bg-info' : 'bg-secondary'}`}
              >
                {scope.scopeType === 'Country'
                  ? t('Users.Access.Country', 'Country')
                  : t('Users.Access.Dealer', 'Dealer')}
              </span>
              {scope.scopeValue}
              {conflicts && (
                <span className="ms-2 text-warning-emphasis fs-sm">
                  {t('Users.Access.NotAllowed', 'not allowed by the selected roles')}
                </span>
              )}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => onRemove(index)}
              aria-label={t('Users.Access.Remove', 'Remove')}
            >
              {t('Users.Access.Remove', 'Remove')}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
